import { ElMessageBox, ElNotification } from "element-plus";
import { getRequest } from "~/apis/requests/useApiCall";
import { User } from "~/types";
import { Pagination } from "~/types/pagination";
import { AgentBreak } from "~/types/reports/AgentBreak";
import { AgentLogin } from "~/types/reports/AgentLogin";
import { AgentQueue } from "~/types/reports/AgentQueue";
import { AgentUtilization } from "~/types/reports/AgentUtilization";
import { toLocalDateString } from "~/utils/dateHelper";

interface AgentKpi {
  id: number;
  name: string;
  mobile: string | null;
  total_online_time: number;
  utilization: number;
  last_seen: string;
  speed_of_answer: number;
}

const currentDate = new Date();
const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
// create a const for 31 days ago based on month
const thirtyOneDaysAgo = new Date(Date.now() - 31 * 24 * 60 * 60 * 1000);
function formatFilterDateTime(date: Date): string {
  // Local, not UTC: see utils/dateHelper.ts — toISOString() shifts a picked
  // date back a day for anyone east of UTC.
  return toLocalDateString(date);
}

export const useAgentsStore = definePiniaStore("agentsStore", {
  state: () => ({
    showAgentEditModal: false,
    _selectedAgent: {} as User,
    _selectedAgentKpi: {} as AgentKpi,
    _utilizationReportData: [] as AgentUtilization[],
    _utilizationReportPagination: {} as Pagination,
    _loginReportData: [] as AgentLogin[],
    _loginReportsfilterData: {
      from_date: formatFilterDateTime(sevenDaysAgo),
      to_date: formatFilterDateTime(currentDate),
      is_date_wise: true,
    },
    _breakReportData: [] as AgentBreak[],
    _breakReportsfilterData: {
      from_date: formatFilterDateTime(sevenDaysAgo),
      to_date: formatFilterDateTime(currentDate),
      view: "date",
    },
    _utilizationReportsFilterData: {
      from_date: formatFilterDateTime(sevenDaysAgo),
      to_date: formatFilterDateTime(currentDate),
      is_date_wise: true,
    },
    _loginReportPagination: {} as Pagination,
    _queueReportData: [] as AgentQueue[],
    _queueReportPagination: {} as Pagination,
    _simpleAgents: [] as User[],
  }),
  actions: {
    async getSimpleAgents() {
      const { response, error } = await getRequest<User[]>("agents/simple");
      if (!error) {
        this._simpleAgents = response.data;
      }
      return this._simpleAgents;
    },
    toggleAgentEditModal() {
      this.showAgentEditModal = !this.showAgentEditModal;
    },
    setSelectedAgent(agent: User) {
      this._selectedAgent = agent;
    },
    async getAgent(agentId: string | number) {
      const { response, error } = await getRequest<User>(`agents/${agentId}`);
      if (!error) {
        this.setSelectedAgent(response.data);
      }
    },
    async getUtilizationReport() {
      let url = `cc/report/callUtilization?`;
      for (const [key, value] of Object.entries(
        this._utilizationReportsFilterData,
      )) {
        if (!value) continue;
        url += `&${key}=${value}`;
      }
      const { response, error } = await getRequest<AgentUtilization[]>(url);

      if (error) {
        ElMessageBox({
          title: "Error Fetching reports",
          message:
            error?.response?.data?.message || error?.response?.data?.error,
          type: "error",
        });
        this._utilizationReportData = [];
        return;
      }
      if (!error) {
        this._utilizationReportData = response.data;
        ElNotification({
          title: "Filter Applied",
          type: "success",
        });
      }
    },
    setUtilizationReportsFilterData(data: object) {
      this._utilizationReportsFilterData = data;
    },
    async getLoginReport() {
      let url = `reports/agentOnlineDuration?`;
      for (const [key, value] of Object.entries(this._loginReportsfilterData)) {
        if (!value) continue;
        url += `&${key}=${value}`;
      }
      const { response, error } = await getRequest<AgentUtilization[]>(url);

      if (error) {
        ElMessageBox({
          title: "Error Fetching reports",
          message:
            error?.response?.data?.message || error?.response?.data?.error,
          type: "error",
        });
        this._loginReportData = [];
        return;
      }
      if (!error) {
        this._loginReportData = response.data;
        ElNotification({
          title: "Filter Applied",
          type: "success",
        });
      }
    },
    async getBreakReport() {
      let url = `reports/agentBreakDuration?`;
      for (const [key, value] of Object.entries(this._breakReportsfilterData)) {
        if (!value) continue;
        url += `&${key}=${value}`;
      }
      const { response, error } = await getRequest<AgentBreak[]>(url);

      if (error) {
        ElMessageBox({
          title: "Error Fetching reports",
          message:
            error?.response?.data?.message || error?.response?.data?.error,
          type: "error",
        });
        this._breakReportData = [];
        return;
      }

      this._breakReportData = response.data;
      ElNotification({
        title: "Filter Applied",
        type: "success",
      });
    },
    setBreakReportsFilterData(data: object) {
      this._breakReportsfilterData = data;
    },
    setLoginReportsFilterData(data: object) {
      this._loginReportsfilterData = data;
    },
    async getAgentKpi(agentId: string) {
      if (!agentId) return;
      const { from_date, to_date } = this._loginReportsfilterData;
      const { response, error } = await getRequest<AgentKpi>(
        `cc/agentKpis/${agentId}?from_date=${from_date}&to_date=${to_date}`,
      );
      if (!error) {
        this._selectedAgentKpi = response.data;
      }
    },
    async getQueueReport(agentId: string, pageNo = 1) {
      const { response, error } = await getRequest<AgentQueue[]>(
        `cc/agents/${agentId}/report/queue?page=${pageNo}`,
      );

      if (!error) {
        this._queueReportData = response.data;
      }
    },
  },
  getters: {
    selectedAgent: (state) => state._selectedAgent,
    selectedAgentKpi: (state) => state._selectedAgentKpi,
    utilizationReportData: (state) => state._utilizationReportData,
    utilizationReportPagination: (state) => state._utilizationReportPagination,
    loginReportData: (state) => state._loginReportData,
    breakReportData: (state) => state._breakReportData,
    loginReportPagination: (state) => state._loginReportPagination,
    queueReportData: (state) => state._queueReportData,
    _queueReportPagination: (state) => state._queueReportPagination,
    simpleAgents: (state) => state._simpleAgents,
  },
});
